import * as THREE from 'three';
import type { GrainKind } from '@/rendering/DetailShading';
import type { FloraDefinition, FloraGeometryForm } from './FloraDefinition';
import type { FloraInstance } from './FloraInstance';
import { computeGrowthState, type GrowthState } from './FloraGrowthModel';
import { buildFloraGeometry, type FloraGeometryDetail } from './FloraGeometry';
import { createAccentMaterial, createFoliageMaterial, createTrunkMaterial } from './FloraMaterial';
import type { SeasonalRenderState } from './FloraSeasonModel';
import { floraModels, type FloraModelRegistry } from './FloraModelSource';

/**
 * Builds the temporary editable Three.js representation of a flora instance from canonical data
 * (spec §17.2). The returned object is a *view*: it carries `userData.floraInstanceId` back to the
 * domain entity and is rebuilt whenever the instance's maturity/modifiers change.
 *
 * Views are assembled from up to three named parts — `structure`, `foliage`, `accent` — which the
 * season model drives independently. An authored model registered in the
 * {@link FloraModelRegistry} takes priority over the procedural generator; see `FloraModelSource.ts`.
 */

export interface FloraFactoryOptions {
  /** Geometric detail tier for newly built views. */
  detail?: FloraGeometryDetail;
  /** Authored-model registry consulted before the procedural generator. */
  models?: FloraModelRegistry;
}

/** Whether a plant ever shows blossoms, seed heads, or fruit — controls accent geometry. */
export function hasFloralInterest(definition: FloraDefinition): boolean {
  const { seasons } = definition;
  if (seasons.bloomWindows && seasons.bloomWindows.length > 0) return true;
  return (['spring', 'summer', 'autumn', 'winter'] as const).some((key) => {
    const appearance = seasons[key];
    return (
      (appearance.flowerColor !== undefined && (appearance.flowerDensity ?? 0) > 0) ||
      (appearance.fruitColor !== undefined && (appearance.fruitDensity ?? 0) > 0)
    );
  });
}

/**
 * What a form's foliage is actually made of, for `DetailShading`.
 *
 * This mapping is the whole answer to "a tuft of leaves and the lawn are both green". The silhouette
 * already separates them at a distance; up close what separates them is that one is clumps of
 * broadleaf and the other is strands, and that is a fact about the *form*, which is exactly what this
 * table records. Everything with a canopy or a crown of leaves is leafy; conifers are needled; the
 * grasses and the mats that behave like them are bladed.
 */
export function grainForForm(form: FloraGeometryForm): GrainKind {
  switch (form) {
    case 'conical-tree':
      return 'needled';
    case 'grass-tuft':
    case 'groundcover-mat':
      return 'bladed';
    case 'round-canopy-tree':
    case 'mounding-shrub':
    case 'perennial-clump':
      return 'leafy';
  }
}

export class FloraFactory {
  private readonly detail: FloraGeometryDetail;
  private readonly models: FloraModelRegistry;

  constructor(options: FloraFactoryOptions = {}) {
    this.detail = options.detail ?? 'high';
    this.models = options.models ?? floraModels;
  }

  /** Build an Object3D for an instance. */
  build(definition: FloraDefinition, instance: FloraInstance): THREE.Object3D {
    const growth = this.growthState(definition, instance);
    const group = new THREE.Group();
    group.name = 'flora';
    group.userData.floraInstanceId = instance.id;

    const provider = this.models.resolve(definition.id);
    const authored = provider?.build({ definition, instance, growth, detail: this.detail }) ?? null;
    if (authored) {
      group.userData.floraModelProviderId = provider?.id;
      group.add(authored);
    } else {
      this.addProceduralParts(group, definition, instance, growth);
    }

    this.applyTransform(group, instance);
    return group;
  }

  private addProceduralParts(
    group: THREE.Object3D,
    definition: FloraDefinition,
    instance: FloraInstance,
    growth: GrowthState,
  ): void {
    const parts = buildFloraGeometry(definition.rendering.geometryForm, growth, instance.visualSeed, {
      detail: this.detail,
      accents: hasFloralInterest(definition),
    });

    if (parts.structure) {
      const material = createTrunkMaterial({
        colorVariation: instance.colorVariation,
        seed: instance.visualSeed,
        ...(definition.rendering.trunkColor !== undefined
          ? { trunkColor: definition.rendering.trunkColor }
          : {}),
      });
      group.add(this.mesh(parts.structure, material, 'structure'));
    }

    if (parts.foliage) {
      const material = createFoliageMaterial({
        foliageColor: definition.seasons.summer.foliageColor,
        colorVariation: instance.colorVariation,
        grain: grainForForm(definition.rendering.geometryForm),
        seed: instance.visualSeed,
      });
      group.add(this.mesh(parts.foliage, material, 'foliage'));
    }

    if (parts.accent) {
      const accent = this.mesh(parts.accent, createAccentMaterial(), 'accent');
      // Hidden until the season model reports an active bloom or fruit window.
      accent.visible = false;
      group.add(accent);
    }
  }

  private mesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    name: string,
  ): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  /** Apply an instance's position/rotation to its object. */
  applyTransform(object: THREE.Object3D, instance: FloraInstance): void {
    object.position.set(instance.position.x, instance.position.y, instance.position.z);
    object.rotation.set(instance.rotation.x, instance.rotation.y, instance.rotation.z);
  }

  /**
   * Apply a resolved seasonal appearance to a flora view: foliage colour and presence, and blossom /
   * fruit accents. Structure is left visible so a bare deciduous plant still reads as branches.
   */
  applySeasonalState(object: THREE.Object3D, state: SeasonalRenderState): void {
    const accentColor = state.flowerDensity >= state.fruitDensity ? state.flowerColor : state.fruitColor;
    const accentDensity = Math.max(state.flowerDensity, state.fruitDensity);

    object.traverse((node) => {
      const material = (node as Partial<THREE.Mesh>).material;
      if (node.name === 'foliage') {
        node.visible = !state.dormant && state.foliageDensity > 0.02;
        if (material instanceof THREE.MeshStandardMaterial) {
          material.color.set(state.foliageColor);
        }
      } else if (node.name === 'accent') {
        node.visible = accentDensity > 0.04 && accentColor !== null;
        if (accentColor !== null && material instanceof THREE.MeshStandardMaterial) {
          material.color.set(accentColor);
        }
      }
    });
  }

  /** Resolve the growth state for an instance, applying its biological modifiers. */
  growthState(definition: FloraDefinition, instance: FloraInstance): GrowthState {
    return computeGrowthState(definition.growth, instance.maturity, {
      sizeVariation: instance.sizeVariation,
      fullness: instance.fullness,
      health: instance.health,
    });
  }

  /** Dispose an object's geometries and materials. */
  dispose(object: THREE.Object3D): void {
    object.traverse((obj) => {
      const mesh = obj as Partial<THREE.Mesh>;
      mesh.geometry?.dispose();
      const material = mesh.material;
      if (Array.isArray(material)) {
        material.forEach((m) => m.dispose());
      } else {
        material?.dispose();
      }
    });
  }
}
